<template>
  <div>
    <!-- Loader displayed when isLoading is true -->
    <div
      v-if="isLoading"
      class="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50"
    >
      <svg
        class="animate-spin h-16 w-16 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
          fill="none"
        />
      </svg>
    </div>

    <!-- Your page content goes here -->
    <div v-else class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
      <table
        id="patients"
        class="w-full z-10 text-left rtl:text-right text-gray-700 display"
      >
        <thead>
          <tr class="bg-gray-100">
            <th scope="col" class="px-6 py-3">
              <div class="flex items-center">#</div>
            </th>
            <th scope="col" class="px-6 py-3">
              <div class="flex items-center">Unique Code</div>
            </th>
            <th scope="col" class="px-6 py-3">
              <div class="flex items-center">Patient Name</div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">Phone</div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">ID/Passport</div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">Visit Type</div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">Age</div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">Registered At</div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">Updated At</div>
            </th>
            <th scope="col" class="px-6">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(patient, key) in paginatedData" :key="key" class="border-b">
            <td class="px-6 py-2">{{ (currentPage - 1) * itemsPerPage + key + 1 }}</td>
            <td class="px-6 py-2">{{ patient.unique_code }}</td>
            <td class="px-6 py-2">{{ patient.FullName }}</td>
            <td class="px-2 py-2">{{ patient.PhoneNumber || "N/A"}}</td>
            <td class="px-6 py-2">{{ patient.IdNumber  || "N/A"}}</td>
            <td class="px-6 py-2">{{ patient.VisitType }}</td>
            <td class="px-6 py-2">
              {{ patient.Age }}
              {{ patient.AgeType == 1 ? "Year(s)" : "Month(s)" }}
            </td>
            <td class="px-6 py-2">
              {{ formatDate(patient?.created_at) }}
            </td>
            <td class="px-6 py-2">
              {{ formatDate(patient?.updated_at) }}
            </td>
            <td class="px-6 space-x-2 py-2">
              <button
                @click="handleShowDetails(patient.PatientId)"
                class="bg-custom-yellow text-white px-4 py-1 rounded"
              >
                View Patient
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <PaginationComponent
      v-model:currentPage="currentPage"
      v-model:itemsPerPage="itemsPerPage"
      :totalPages="totalPages"
      :totalItems="totalItems"
      @update:currentPage="goToPage"
      @update:changePageSize="changePageSize"
    />

    <CreatePatientVisitModal
      :show="showVisitModal"
      :formData="patienRegForm"
      :patient="newVisitPatient"
    />
  </div>
</template>

<script>
import apiClient from "@/services/api";
import PaginationComponent from "@/components/shared/pagination.vue";
import CreatePatientVisitModal from "../modals/CreatePatientVisit.Modal.vue";

export default {
  name: "ReceptionPatientQueueTab",
  components: {
    CreatePatientVisitModal,
    PaginationComponent,
  },
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      patients: [],
      selectedPatientId: null,
      showVisitModal: false,
      isLoading: false,
      newVisitPatient: null,
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      totalPages: 0,
    };
  },
  mounted() {
    this.fetchPatientData();
  },
  computed: {
    paginatedData() {
      // Backend already returns paginated data
      return this.patients;
    },
  },
  methods: {
    async fetchPatientData() {
      try {
        this.isLoading = true;
        const response = await apiClient.getPatientList({
          page: this.currentPage,
          per_page: this.itemsPerPage
        });

        this.isLoading = false;
        if (response.success) {
          this.patients = response.data;
          this.totalItems = response.meta.total_items;
          this.totalPages = response.meta.total_pages;
          this.currentPage = response.meta.current_page;
          this.itemsPerPage = response.meta.per_page;
        } else {
          this.$toast.error(
            "Unable to fetch patients queue, please check your internet connection."
          );
        }
      } catch (error) {
        this.isLoading = false;
        this.$toast.error(
          "Unable to fetch patients queue, please check your internet connection."
        );
      }
    },
    async searchPatientData(params) {
      try {
        this.isLoading = true;
        const response = await apiClient.searchPatient(params);

        if (response.success) {
          this.patients = response.data;
          // Search may not return pagination meta, so reset to defaults
          this.totalItems = response.data.length;
          this.totalPages = 1;
          this.currentPage = 1;
        }

        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.$toast.error(
          "Unable to fetch patients, please check your internet connection."
        );
      }
    },
    handleShowDetails(patientId) {
      this.$router.push({
        name: "PatientInformationPage",
        params: { patientId: patientId },
      });
    },
    handleVisitCreation(patientId) {
      this.showVisitModal = true;
      this.newVisitPatient = patientId;
      console.log(patientId);
    },
    goToPage(pageNumber) {
      console.log("goToPage", pageNumber);
      this.currentPage = pageNumber;
      this.fetchPatientData();
    },
    changePageSize(pageSize) {
      console.log("change page size", pageSize);
      this.itemsPerPage = pageSize;
      this.currentPage = 1; // Reset to page 1 when changing page size
      this.fetchPatientData();
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      };
      const date = new Date(dateString);
      return date.toLocaleString("en-US", options);
    },
  },
};
</script>

<style lang="css" scoped></style>